// Composant à intégrer dans ShopSettingsPage
// Onglet CGV — éditeur de texte + aperçu

import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Loader2, Save, CheckCircle2, FileText, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CGV_DEFAULT = `CONDITIONS GÉNÉRALES DE VENTE ET DE RÉPARATION — AMCPHONE MARTINIQUE

Article 1 — Objet
Les présentes Conditions Générales de Vente et de Réparation s'appliquent à toutes les prestations de réparation, vente de produits et services proposés par AMCPhone Martinique, dont le siège social est situé à Saint-Joseph, Martinique. Toute acceptation d'un devis ou dépôt d'un appareil implique l'acceptation pleine et entière des présentes CGV.

Article 2 — Devis et acceptation
Tout devis émis par AMCPhone Martinique est valable 30 jours à compter de sa date d'émission. Passé ce délai, AMCPhone Martinique se réserve le droit de modifier les tarifs proposés sans préavis. La signature du devis par le client, qu'elle soit manuscrite ou électronique, vaut acceptation pleine et entière des prestations, tarifs et conditions mentionnés.

Article 3 — Dépôt de l'appareil
Le client s'engage à déposer un appareil dont il est le propriétaire ou pour lequel il dispose d'une autorisation expresse du propriétaire. AMCPhone Martinique se réserve le droit de refuser tout appareil dont l'origine lui semblerait douteuse. Un reçu de dépôt sera remis au client lors de chaque prise en charge.

Article 4 — Données personnelles et confidentialité
Le client est seul responsable de la sauvegarde de ses données personnelles, photos, contacts, applications et tout autre contenu présent sur l'appareil avant son dépôt. AMCPhone Martinique ne saurait être tenu responsable de toute perte, corruption ou divulgation de données survenant avant, pendant ou après l'intervention.

Article 5 — Pièces détachées et matériaux
Les pièces utilisées sont des pièces de qualité compatible garanties sauf mention contraire sur le devis. Des pièces d'origine constructeur peuvent être proposées sur demande expresse et feront l'objet d'un devis spécifique.

Article 6 — Garantie
Toutes les réparations effectuées par AMCPhone Martinique sont garanties pendant une durée de 3 mois à compter de la date de restitution de l'appareil. Cette garantie couvre exclusivement les défauts liés à la main-d'œuvre et aux pièces fournies. La garantie est annulée en cas de chute, oxydation, tentative de réparation par un tiers, ou dommages causés après intervention.

Article 7 — Délais d'intervention
Les délais d'intervention communiqués sont donnés à titre indicatif et ne constituent pas un engagement contractuel. Le client sera informé de tout dépassement significatif du délai estimé.

Article 8 — Appareils non récupérés
Tout appareil non récupéré dans un délai de 30 jours calendaires suivant la notification de fin de réparation sera considéré comme abandonné. AMCPhone Martinique adressera une mise en demeure. En l'absence de réponse sous 15 jours, AMCPhone Martinique se réserve le droit de disposer de l'appareil.

Article 9 — Tarifs et paiement
Les tarifs sont indiqués en euros toutes taxes comprises. Le règlement est exigible à la remise de l'appareil réparé. En cas de refus de paiement, AMCPhone Martinique se réserve le droit d'exercer son droit de rétention sur l'appareil jusqu'au complet règlement. Modes de paiement acceptés : espèces, carte bancaire, virement bancaire.

Article 10 — Responsabilité
La responsabilité d'AMCPhone Martinique est limitée au montant de la prestation facturée. AMCPhone Martinique ne saurait être tenu responsable de tout dommage indirect, perte d'exploitation ou préjudice immatériel résultant d'une panne, d'un dysfonctionnement ou d'une réparation.

Article 11 — Protection des données personnelles (RGPD)
Les informations collectées lors de la prise en charge sont utilisées exclusivement dans le cadre de la gestion de la réparation et de la relation client. Conformément au RGPD, le client dispose d'un droit d'accès, de rectification et de suppression de ses données personnelles.

Article 12 — Litiges et juridiction compétente
En cas de litige, les parties s'engagent à rechercher en priorité une solution amiable. À défaut d'accord dans un délai de 30 jours, le tribunal judiciaire de Fort-de-France (Martinique) sera seul compétent.`;

export default function CGVSettings() {
  const [cgv, setCgv]       = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  const token = () => localStorage.getItem("token");

  useEffect(() => { fetchCGV(); }, []);

  const fetchCGV = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/cgv`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setCgv(res.data.cgv || CGV_DEFAULT);
    } catch {
      setCgv(CGV_DEFAULT);
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/cgv`, { cgv }, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      setSaved(true);
      toast.success("CGV enregistrées ✅");
      setTimeout(() => setSaved(false), 3000);
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    if (window.confirm("Remettre les CGV par défaut ?")) {
      setCgv(CGV_DEFAULT);
      toast.info("CGV remises par défaut — pensez à enregistrer");
    }
  };

  if (loading) return (
    <div className="flex justify-center py-8">
      <Loader2 className="w-6 h-6 animate-spin text-primary" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-semibold text-foreground">Conditions Générales de Vente</h3>
            <p className="text-xs text-muted-foreground">Affichées sur les devis PDF et la page de signature client</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} className="text-xs">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Réinitialiser
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-primary text-xs">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> :
             saved  ? <CheckCircle2 className="w-3.5 h-3.5 mr-1.5 text-emerald-400" /> :
                      <Save className="w-3.5 h-3.5 mr-1.5" />}
            {saved ? "Enregistré !" : "Enregistrer"}
          </Button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-lg p-3">
        💡 Ces CGV sont automatiquement intégrées dans tous vos devis PDF et affichées sur la page de signature en ligne de vos clients.
      </div>

      <textarea
        value={cgv}
        onChange={e => setCgv(e.target.value)}
        rows={25}
        className="w-full p-4 rounded-xl bg-secondary/30 border border-border text-sm text-foreground font-mono resize-y focus:outline-none focus:ring-2 focus:ring-primary/30 leading-relaxed"
        placeholder="Rédigez vos conditions générales..."
      />

      <div className="text-xs text-muted-foreground text-right">
        {cgv.length} caractères
      </div>
    </div>
  );
}
